import React from 'react';
import { Copy, Check } from 'lucide-react';
import { useState } from 'react';
import { useResponseFormatting } from './ResponseFormattingContext';

// Import syntax highlighter directly
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';

interface CodeBlockProps {
  code: string;
  language?: string;
  filename?: string;
}

export const CodeBlock: React.FC<CodeBlockProps> = ({ code, language = 'text', filename }) => {
  const { settings } = useResponseFormatting();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy code:', error);
    }
  };

  // If syntax highlighting is disabled, show plain code
  if (!settings.syntaxHighlighting) {
    return (
      <div className="relative group bg-muted rounded-lg border">
        {filename && (
          <div className="px-4 py-2 bg-muted/50 border-b rounded-t-lg">
            <span className="text-sm font-medium text-muted-foreground">{filename}</span>
          </div>
        )}
        <div className="relative">
          <pre className="p-4 overflow-x-auto text-sm">
            <code className="text-foreground">{code}</code>
          </pre>
          <button
            onClick={handleCopy}
            className="absolute top-2 right-2 p-2 bg-background/80 hover:bg-background border rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
            title="Copy code"
          >
            {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative group bg-muted rounded-lg border">
      {filename && (
        <div className="px-4 py-2 bg-muted/50 border-b rounded-t-lg">
          <span className="text-sm font-medium text-muted-foreground">{filename}</span>
        </div>
      )}
      <div className="relative">
        <SyntaxHighlighter
          language={language}
          customStyle={{
            margin: 0,
            padding: '1rem',
            background: 'transparent',
            fontSize: '0.875rem',
          }}
          wrapLongLines={true}
        >
          {code}
        </SyntaxHighlighter>
        <button
          onClick={handleCopy}
          className="absolute top-2 right-2 p-2 bg-background/80 hover:bg-background border rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
          title="Copy code"
        >
          {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
};
